import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Search, Loader2, ArrowLeft, BadgeCheck, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { api, formatApiError, PHONE, PHONE_RAW } from "@/lib/api";

export default function ReportPortal() {
  const [ref, setRef] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setBooking(null);
    if (!ref.trim()) return toast.error("Enter your booking reference ID");
    if (!/^\d{10}$/.test(phone)) return toast.error("Enter the 10-digit phone number used at booking");
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/track", { params: { ref: ref.trim(), phone } });
      setBooking(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/download", { params: { ref: ref.trim(), phone }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Hemozy-Report-${booking.ref}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error("Could not download the report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B192C] dot-grid spotlight grain" data-testid="report-portal-page">
      <header className="max-w-7xl mx-auto px-5 lg:px-8 h-[70px] flex items-center justify-between">
        <Link to="/"><Logo light /></Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="portal-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
      </header>

      <main className="max-w-xl mx-auto px-5 py-14 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-[#D92638]/15 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#D92638]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Your report, one tap away.</h1>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed mb-8">
            Enter the reference ID from your booking confirmation and the phone number you booked with.
          </p>

          <form onSubmit={lookup} className="bg-white rounded-[1.75rem] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]" data-testid="report-lookup-form">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3" data-testid="report-lookup-error">{error}</div>
            )}
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Booking reference ID</label>
            <input value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="HZ-XXXXXXXX" data-testid="report-ref-input"
              className="mt-1.5 mb-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-mono-med focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone number</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" placeholder="10-digit mobile" data-testid="report-phone-input"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
            <button type="submit" disabled={loading} data-testid="report-lookup-button"
              className="mt-6 w-full flex items-center justify-center gap-2 bg-[#0B192C] hover:bg-[#1E3E62] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? "Looking up..." : "Find my booking"}
            </button>
          </form>

          {booking && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-white rounded-[1.75rem] p-7 shadow-xl" data-testid="report-booking-card">
              <div className="flex items-center justify-between">
                <span className="font-mono-med font-bold text-[#0B192C]">{booking.ref}</span>
                <span className={`text-[11px] font-bold px-3 py-1 rounded-full border ${booking.status === "completed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : booking.status === "confirmed" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-amber-100 text-amber-700 border-amber-200"}`} data-testid="report-booking-status">
                  {booking.status.toUpperCase()}
                </span>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Patient</span><span className="font-medium text-[#0B192C]">{booking.patient.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Tests</span><span className="font-medium text-[#0B192C] text-right max-w-[60%]">{booking.items.map((i) => i.name).join(", ")}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Slot</span><span className="font-medium text-[#0B192C]">{booking.slot_date} · {booking.slot_time}</span></div>
              </div>
              <div className="mt-5 border-t border-slate-100 pt-5">
                {booking.has_report ? (
                  <button onClick={download} disabled={downloading} data-testid="report-download-button"
                    className="w-full flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {downloading ? "Downloading..." : "Download report (PDF)"}
                  </button>
                ) : (
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3.5" data-testid="report-not-ready">
                    <Clock3 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-700 leading-relaxed">
                      Your report isn't uploaded yet. Routine tests are reported same day; hematopathology in 24-48 hrs. Questions? WhatsApp us at <a href={`https://wa.me/${PHONE_RAW}`} className="font-semibold underline">{PHONE}</a>.
                    </p>
                  </div>
                )}
              </div>
              {booking.has_report && (
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#0D9488]" /> Report uploaded {booking.report_uploaded_at?.slice(0, 10)} · Specialist-signed
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
